import React, { Component, PropTypes } from 'react'
import styled from 'styled-components'
import R from 'ramda'
import Grid, { Half } from 'grid-styled'
import { translate } from 'react-i18next'
import CardContainer from '../../components/CardContainer'
import { Metrics, Fonts } from '../../Themes'
import Input from '../../components/Input'
import Button from '../../components/Button'
import { validateInput, validateForm } from '../../Utils/Validations'
import { generateAmbassadorAgreement } from '../../sources/ContractsSource'
import Loader from '../../components/Loader'

const CustomGrid = styled(Grid)`
  padding-left: ${Metrics.margins.medium};
  padding-right: ${Metrics.margins.medium};
  button {
    margin-top: ${Metrics.margins.base};
    float: right
  }

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    padding: 0;
  }
`

const CustomHalf = styled(Half)`
  margin-top: ${props => (props.noPadTop ? 0 : Metrics.margins.base)};
  padding-left: ${Metrics.margins.medium};
  padding-right: ${Metrics.margins.medium};

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    padding: 0;
  }
`

const CustomCardContainer = styled(CardContainer)`
  height: 100%;
`

const List = styled.ul`
  padding-left: 0;
  list-style: none;
  ${Fonts.style.Text};

  li {
    margin: ${Metrics.margins.base} 0;
    padding-bottom: ${Metrics.margins.small};
    border-bottom: ${Metrics.borders.default};

    li {
      padding-bottom: 0;
      margin: ${Metrics.margins.small} 0;
      border-bottom: none;
    }
  }
`

const OrderedList = styled.ol`
  padding-left: 0;
  ${Fonts.style.Text};

  counter-reset: item;
  li:before {
    content: counters(item, ".") ".";
    counter-increment: item;
  }
  ol {
    padding-left: ${Metrics.margins.medium};
  }

  li {
    display: block;
    padding-bottom: ${Metrics.margins.small};
    border-bottom: none;
  }
`

const Terms = styled.div`
  ${Fonts.style.Text};
  margin-top: ${Metrics.margins.base};
`

const Title = styled.div`
  margin-bottom: ${Metrics.margins.small};
  ${Fonts.style.Label};
`

class CollaborationAgreement extends Component {
  constructor (props) {
    super(props)
    this.state = {
      name: '',
      company: '',
      address: '',
      phoneNumber: '',
      email: '',
      function: '',
      generating: false,
      errors: {
        name: '',
        company: '',
        address: '',
        phoneNumber: '',
        email: '',
        function: ''
      }
    }
    this.handleChange = this.handleChange.bind(this)
    this.downloadFile = this.downloadFile.bind(this)
    this.handleValidate = this.handleValidate.bind(this)
  }

  handleChange (event, key) {
    const state = R.clone(this.state)
    state[key] = event.target.value
    this.setState(state)
  }

  downloadFile () {
    const { errors } = this.state
    let validation = validateForm(this.state, errors)

    if (validation.valid) {
      this.setState({ generating: true }, () => {
        generateAmbassadorAgreement({
          name: this.state.name,
          company: this.state.company,
          address: this.state.address,
          phoneNumber: this.state.phoneNumber,
          email: this.state.email,
          function: this.state.function
        })
          .then(response => {
            this.setState({ generating: false })
            document.location = response.data.url
          })
          .catch(console.error)
      })
    } else {
      this.setState({ errors: validation.errors })
    }
  }

  handleValidate (name, value, errors) {
    let newErrors = validateInput(name, value, errors)
    this.setState({ errors: newErrors })
  }

  render () {
    const { t } = this.props
    const { errors } = this.state
    let keys = [
      { key: 'name', label: t('Name'), placeholder: t('Name') },
      { key: 'company', label: t('Company'), placeholder: t('Company Name') },
      {
        key: 'address',
        label: t('Address'),
        placeholder: t('Company Address')
      },
      { key: 'phoneNumber', label: t('Phone'), placeholder: t('Phone Number') },
      { key: 'email', label: t('Email'), placeholder: t('E-mail') },
      { key: 'function', label: t('Function'), placeholder: t('Function') }
    ]

    return (
      <CustomCardContainer title={t('NDA & Ambassador Agreement')}>
        <Loader visible={this.state.generating} />
        <CustomGrid>
          <Terms>
            <p>
              Whereby the Ambassador wishes to introduce Business Associates and
              Clients to HvB in quality of HvB Ambassador.
            </p>
            <p>
              Whereby the Parties wish to maintain the confidentiality and/or
              the proprietary nature of the Confidential Information disclosed.
            </p>
            Whereby the Parties wish to set the basis for a lasting business
            relationship.
            <List>
              <li>
                <Title>Definitions</Title>
                <OrderedList>
                  <li>
                    “Business Associates” means, business affiliates, investors,
                    financers, Clients, employees, vendors, asset owners,
                    Intermediaries and any other third party relations.
                  </li>
                  <li>
                    “Client” means third parties directly interested in
                    acquiring or selling real estate or other assets.
                  </li>
                  <li>
                    “Confidential Information” means any and all information
                    disclosed orally or in writing or made available by any
                    Party ("Disclosing Party") to another Party ("Recipient") so
                    long as not explicitly designated as not confidential by the
                    Disclosing Party. In addition, the existence and terms of
                    this Agreement shall also be treated as Confidential
                    Information.
                  </li>
                  <li>
                    “Investment” means, any transfer of funds or any other
                    assets, of which the value can be determined, in view to
                    purchase, grant a loan, lease, or otherwise benefit the
                    owner of an asset.
                  </li>
                  <li>
                    “Transaction” means, the sale of one (1) asset either
                    directly listed in HvB’s portfolio or sold through or by a
                    Business Associate of HvB.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Confidentiality and Non-Disclosure</Title>
                <OrderedList>
                  <li>
                    The Parties agree to maintain the confidentiality of the
                    Confidential Information and to prevent its unauthorized
                    dissemination or use, subject to the exceptions enumerated
                    in Section 2.5. of this Agreement.
                  </li>
                  <li>
                    The Parties agree not to use the Confidential Information
                    for purposes other than that necessary to consider and
                    enable the business relationship or pursue transaction
                    between or involving the Parties or one of the Parties.
                  </li>
                  <li>
                    The Recipient shall protect the Confidential Information by
                    using the same degree of care, but no less than reasonable
                    care, to prevent the unauthorized use, dissemination or
                    publication of the Confidential Information as the Recipient
                    uses to protect its own Confidential Information.
                  </li>
                  <li>
                    The Recipient shall limit its internal disclosure of the
                    Confidential Information to only those employees,
                    accountants, attorneys and affiliates who have a need to
                    know the information for the limited purpose of the proposed
                    business relationship or transaction between the Parties.
                  </li>
                  <li>
                    This Agreement shall impose no obligations with respect to
                    Confidential Information which:
                    <OrderedList>
                      <li>
                        is disclosed with the prior written consent of the
                        Disclosing Party;
                      </li>
                      <li>
                        is now, or hereafter becomes, through no act or failure
                        to act on the part of the Recipient, generally known or
                        available to the public;
                      </li>
                      <li>
                        was already known by Recipient before receiving such
                        information from the Disclosing Party, as evidenced by
                        written documents;
                      </li>
                      <li>
                        is information which the Recipient can document was
                        independently developed by the Recipient without
                        reference to or use of any Confidential Information
                        received from the Disclosing Party;
                      </li>
                      <li>
                        is required to be disclosed pursuant governmental
                        agencies or law. In such cases, the Recipient seeking to
                        disclose Confidential Information will provide to the
                        Disclosing Party notice prior to such disclosure so as
                        to afford the Disclosing Party a reasonable opportunity
                        to file objections to the disclosure with the
                        appropriate agency or entity.
                      </li>
                    </OrderedList>
                  </li>
                  <li>
                    The Recipient acknowledges and agrees that all the
                    Confidential Information of the Disclosing Party is and
                    shall remain the sole and exclusive property of the
                    Disclosing Party, free of any and all claims of the
                    Recipient.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Ambassador</Title>
                <OrderedList>
                  <li>
                    The Ambassador is authorised to act as an Ambassador of HvB.
                    In doing so, the Ambassador shall act with utmost
                    professionalism and diligence and in respect of the values
                    of HvB.
                  </li>
                  <li>
                    The Ambassador herewith explicitly permits HvB, after
                    successful introduction of his Business Associates, to
                    contact Business Associates directly, for the purpose to
                    consider and enable or pursue a Transaction involving that
                    Business Associate.
                  </li>
                  <li>
                    The Ambassador is not authorized to represent HvB or
                    conclude any agreement for or in the name of HvB, without
                    the prior explicit written consent of HvB.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Ambassador Fee</Title>
                <OrderedList>
                  <li>
                    In the case of a successful Transaction including a Business
                    Associate introduced to HvB by the Ambassador, the
                    Ambassador is entitled to receive a commission fee. No
                    Ambassador Fee applies if HvB is already in contact with the
                    Business Associate or already has knowledge of the
                    availability of the asset prior to the introduction by the
                    Ambassador.
                  </li>
                  <li>
                    The commission fee is structured as follows:
                    <OrderedList>
                      <li>
                        In case of a direct introduction to an investor or the
                        owner of an asset, the ambassador commission is of 0.1%
                        of the total Transaction value, capped at CHF 100’000
                        per successful Transaction, for the first 3
                        Transactions.
                      </li>
                      <li>
                        In case of a direct introduction to an intermediary,
                        directly representing an investor or the owner of an
                        asset, the ambassador commission is of 0.05% of the
                        total Transaction value, capped at CHF 50’000 per
                        successful Transaction, for the first 3 Transactions.
                      </li>
                      <li>
                        In case of an introduction to any other Business
                        Associate, other than those described in clauses 4.2.1.
                        and 4.2.2. above, the ambassador commission is of 0.025%
                        of the total Transaction value, capped at CHF 25’000,
                        only for the first Transaction. The same applies for the
                        simple delivery of contact information, not publically
                        available, of investors or asset owners or their direct
                        representatives, without corresponding introduction.
                      </li>
                    </OrderedList>
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Validity and Termination</Title>
                <OrderedList>
                  <li>
                    This Agreement shall be valid once signed by both Parties.
                  </li>
                  <li>
                    The obligations in this Agreement are continuing and shall
                    survive the termination of any discussions or negotiations
                    between the Parties.
                  </li>
                  <li>
                    This Agreement may be terminated by either Party for any
                    reason and at any time, by written notice given to the other
                    Party. However, any such termination shall have no effect on
                    the rights and obligations of the Parties under this
                    Agreement in respect to Transactions executed, or in the
                    process of being executed prior to such termination and
                    especially with regards to Confidential Information client
                    and contact protection.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Amendments</Title>
                <OrderedList>
                  <li>
                    Any amendment of this Agreement shall only be valid if made
                    in written form and if signed by both Parties.
                  </li>
                </OrderedList>
              </li>
              <li>
                <Title>Governing law and jurisdiction</Title>
                <OrderedList>
                  <li>
                    This Agreement shall in all respects be governed by and
                    construed in accordance with Swiss law, without giving
                    effect to its conflict of law principles. All disputes
                    arising from or in connection with this Agreement, including
                    disputes regarding its conclusion, validity, binding effect,
                    amendment, breach, termination or rescission, shall be
                    subject to the exclusive jurisdiction of courts of the
                    Canton of Zurich.
                  </li>
                </OrderedList>
              </li>
            </List>
          </Terms>
        </CustomGrid>

        {keys.map((el, index) => {
          return (
            <CustomHalf
              key={el.key}
              noPadLeft={index % 2 === 0}
              noPadRight={index % 2 === 1}>
              <Input
                placeholder={el.placeholder}
                name={el.key}
                required
                title={el.label}
                value={this.state[el.key]}
                handleChange={event => this.handleChange(event, el.key)}
                error={errors[el.key]}
                handleBlur={event =>
                  this.handleValidate(el.key, this.state[el.key], errors)}
              />
            </CustomHalf>
          )
        })}
        <CustomGrid>
          <Button label={t('Download')} onClick={this.downloadFile} />
        </CustomGrid>
      </CustomCardContainer>
    )
  }
}

CollaborationAgreement.propTypes = {
  t: PropTypes.func,
  router: PropTypes.object,
  children: PropTypes.node
}

export default translate([ 'common', 'forms', 'admin' ], { wait: true })(
  CollaborationAgreement
)



// WEBPACK FOOTER //
// ./src/pages/Agreements/AmbassadorAgreement.js